import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Box, IconButton } from '@mui/material';
import React from 'react';
import { getCardColors } from '../cardColors';
import { CardIconBackground } from './CardIconBackground';
/**
 * Renders a card icon area with an optional interactive checkbox button.
 *
 * This component displays a colored left section of a card containing an icon or
 * a checkbox that can be toggled via user interaction. When `onIconClick` is provided,
 * the icon area becomes interactive.
 *
 * @param {CardIconCheckboxProps} root0 - Component props.
 * @param {React.ReactNode} root0.icon - The icon to render when unchecked.
 * @param {boolean} [root0.checked] - Indicates whether the checkbox is checked.
 * @param {string} [root0.color] - The key used to determine the background color of the icon area.
 * @param {() => void} [root0.onIconClick] - Callback triggered when the icon button is clicked.
 * @param {string} [root0.iconAriaLabel] - Accessibility label for the icon button.
 * @param {string} [root0.iconColor] - Optional override for the icon color when unchecked.
 *
 * @returns {React.ReactElement} A box element with an icon or checkbox used within card layouts.
 */
export const CardIconCheckbox = ({ checked, icon, color, onIconClick, iconAriaLabel, iconColor }) => {
    const backgroundColor = getCardColors()[color];
    const sx = { display: 'flex', alignItems: 'center', justifyContent: 'center' };
    const iconBackgroundColor = iconColor ? getCardColors()[iconColor] : undefined;
    return (React.createElement(Box, { sx: Object.assign({ position: 'relative', alignSelf: 'stretch', flexShrink: 0, width: 80, minHeight: 80, backgroundColor: backgroundColor, borderTopLeftRadius: 4, borderBottomLeftRadius: 4 }, sx), "data-test": 'icon' },
        React.createElement(CardIconBackground, null),
        React.createElement(Box, { sx: Object.assign({ position: 'relative', color: backgroundColor }, sx) }, onIconClick ? (React.createElement(IconButton, { onClick: (event) => {
                onIconClick();
                event.stopPropagation();
            }, sx: {
                color: checked ? 'primary.main' : (iconBackgroundColor !== null && iconBackgroundColor !== void 0 ? iconBackgroundColor : backgroundColor),
                '& .card-icon': {
                    display: 'flex',
                },
                '& .card-checkbox': {
                    display: 'none',
                },
                '&:hover': {
                    color: 'primary.main',
                    '& .card-icon': {
                        display: 'none',
                    },
                    '& .card-checkbox': {
                        display: 'flex',
                    },
                },
            }, role: 'checkbox', "aria-label": iconAriaLabel, "aria-checked": checked },
            React.createElement("div", { className: 'card-icon' }, checked ? React.createElement(CheckBoxIcon, null) : icon),
            React.createElement("div", { className: 'card-checkbox' }, checked ? React.createElement(CheckBoxIcon, null) : React.createElement(CheckBoxOutlineBlankIcon, null)))) : (icon))));
};
